import { Component, effect, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { AddEvent, CancelEvent, EditEvent, GridComponent, GridModule, SaveEvent } from '@progress/kendo-angular-grid';
import { IconsModule } from '@progress/kendo-angular-icons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { NavigationModule } from '@progress/kendo-angular-navigation';
import { BaseComponent } from '../../../../../../../core/shared/common/base.component';
import { WhitelistRequest, WhitelistService } from './service/whitelist.service';
import { environment } from '../../../../../../../../environments/environment';
import { ActivatedRoute } from '@angular/router';
import { WhitelistForm, WhitelistFormService } from './service/whitelist-form.service';
import { Whitelist } from '../../../../../../../core/models/entities';
import { filter } from 'rxjs';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-whitelist',
  standalone: true,
  imports: [LayoutModule, IconsModule, InputsModule, LabelModule, ButtonsModule, DropDownsModule, NavigationModule, GridModule, ReactiveFormsModule, CommonModule],
  templateUrl: './whitelist.component.html',
  styleUrl: './whitelist.component.scss',
})
export class WhitelistComponent extends BaseComponent {
  public formGroup: FormGroup<WhitelistForm> | undefined;
  public formBuilder = inject(FormBuilder);
  public gridService = inject(WhitelistService);
  public route = inject(ActivatedRoute);
  public id!: number;
  private baseUrl = `${environment.apiUrl}/v1/organization/administrative/whitelist`;
  private formService = inject(WhitelistFormService);
  public serviceAccounts = signal<Whitelist[]>([]);
  private editedRowIndex: number | undefined;

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect() {
    effect(
      () => {
        this.route.queryParams.pipe(filter((params) => params['organizationId'])).subscribe((data) => {
          this.id = parseInt(data['organizationId'], 10);
          return this.loadWhitelist(this.id);
        });
      },
      { allowSignalWrites: true },
    );
  }

  private loadWhitelist(orgId: number) {
    this.gridService.url = `${this.baseUrl}(${orgId})/`;
    return this.gridService.read();
  }

  public addHandler($event: AddEvent): void {
    this.closeEditor($event.sender);
    this.formGroup = this.formService.createWhitelistForm();
    $event.sender.addRow(this.formGroup);
  }

  private closeEditor(grid: GridComponent, rowIndex = this.editedRowIndex) {
    if (this.formGroup) {
      grid.closeRow(rowIndex);
      this.formGroup = undefined;
    }
  }

  saveHandler($event: SaveEvent) {
    if (this.formGroup) {
      const formValue = this.formGroup.value;

      const payload: WhitelistRequest = {
        organization_id: this.id,
        whitelist: {
          pk: $event.isNew ? null : formValue?.pk!,
          ip_address: formValue.ip_address!,
          file_hash: formValue.file_hash!,
          domain: formValue.domain!,
          analyst_notes: formValue.analyst_notes!,
        },
      };
      if ($event.isNew) {
        this.gridService.createWhitelist(payload).subscribe({
          next: (response) => {
            this.loadWhitelist(this.id);
            $event.sender.closeRow($event.rowIndex);
          },
          error: (error) => {
            console.error(error);
          },
        });
      } else {
        this.gridService.updateWhitelist(payload.whitelist).subscribe({
          next: (response) => {
            this.loadWhitelist(this.id);
            $event.sender.closeRow($event.rowIndex);
          },
          error: (error) => {
            console.error(error);
          },
        });
      }
    }
  }

  public editHandler($event: EditEvent): void {
    this.formGroup = this.formService.createWhitelistForm($event.dataItem);
    $event.sender.editRow($event.rowIndex, this.formGroup);
  }

  cancelHandler($event: CancelEvent) {
    this.closeEditor($event.sender, $event.rowIndex);
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  public removeHandler($event: any): void {
    this.gridService.deleteWhitelist($event.dataItem.pk).subscribe(() => {
      this.loadWhitelist(this.id);
    });
  }
}
